import type { components } from "@majitap/sdk";
import {
  Button,
  formatDate,
  formatMoney,
  ListView,
  PageHeader,
  TextButton,
  useCursorList,
  zonedInstant,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { DatePicker } from "@majitap/ui/arc/date-picker/date-picker";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { Select } from "@majitap/ui/arc/select/select";
import { Switch } from "@majitap/ui/arc/switch/switch";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { useProductItems } from "../../catalog";
import { useCompanyFormat } from "../../company";
import { isAmount } from "../../customers";
import { messageFor } from "../../error-copy";
import { filterSearch } from "../../list-search";
import { MomentField, momentOf } from "../pricing/rule-form";
import { ConfirmDialog } from "../shared/confirm";
import styles from "./vouchers.module.css";

type Voucher = components["schemas"]["Voucher"];

export const voucherFilters = filterSearch({ show: ["active", "inactive", "all"] as const });

/** "10% off (at most KES 500.00)", "KES 300.00 off". The amounts are the API's strings. */
export function offerText(v: Pick<Voucher, "discount_type" | "amount" | "max_discount">, money: (s: string) => string) {
  if (v.discount_type === "percent")
    return `${v.amount}% off${v.max_discount ? ` (at most ${money(v.max_discount)})` : ""}`;
  return `${money(v.amount)} off`;
}

/** "3 of 100 used", "12 used". */
export const usageText = (v: Pick<Voucher, "redemptions" | "usage_limit">) =>
  v.usage_limit === null ? `${v.redemptions} used` : `${v.redemptions} of ${v.usage_limit} used`;

/** A voucher never used can be deleted; one that was used is switched off instead (`VOUCHER_IN_USE`). */
export const voucherActionsFor = (v: Pick<Voucher, "redemptions" | "is_active">) => ({
  deactivate: v.is_active,
  activate: !v.is_active,
  remove: v.redemptions === 0,
});

/** Vouchers (P8-20, `Console / Vouchers`): discount codes customers enter when they order. */
export function VouchersPage(): ReactNode {
  const search = useSearch({ from: "/console/vouchers" });
  const navigate = useNavigate({ from: "/vouchers" });
  const format = useCompanyFormat();
  const queryClient = useQueryClient();
  const items = useProductItems();
  const show = search.show ?? "active";
  const [dialog, setDialog] = useState<
    { kind: "edit"; voucher?: Voucher } | { kind: "toggle" | "remove"; voucher: Voucher } | null
  >(null);
  const list = useCursorList({
    queryKey: ["vouchers", show],
    fetchPage: (cursor) =>
      ok(
        api.GET("/v1/admin/pricing/vouchers", {
          params: {
            query: { active: show === "all" ? undefined : show === "active" ? "true" : "false", cursor, limit: 25 },
          },
        }),
      ),
  });
  const money = (v: string) => formatMoney(v, format);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["vouchers"] });
  return (
    <>
      <PageHeader
        title="Vouchers"
        description="Discount codes customers enter when they order."
        actions={<Button onClick={() => setDialog({ kind: "edit" })}>New voucher</Button>}
      />
      <ListView
        caption="Vouchers"
        itemName={{ one: "voucher", other: "vouchers" }}
        rows={list.rows}
        rowKey={(v) => v.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={false}
        empty={
          show === "active"
            ? { title: "No active vouchers", description: "Make one with New voucher." }
            : { title: "No vouchers here", description: "Vouchers you switch off show under Inactive." }
        }
        toolbar={
          <SegmentedControl
            label="Show"
            value={show}
            onValueChange={(v) =>
              navigate({ search: { show: v === "active" ? undefined : (v as "inactive" | "all") } })
            }
            options={[
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
              { value: "all", label: "All" },
            ]}
          />
        }
        columns={[
          {
            key: "code",
            label: "Code",
            render: (v) => (
              <span className={styles.stack}>
                <span className={styles.code}>{v.code}</span>
                {v.description ? <span className={styles.muted}>{v.description}</span> : null}
              </span>
            ),
          },
          {
            key: "amount",
            label: "Offer",
            render: (v) => (
              <span className={styles.stack}>
                <span>{offerText(v, money)}</span>
                <span className={styles.muted}>
                  {[
                    v.product_item_id ? `on ${items.labelOf(v.product_item_id)}` : "on the whole order",
                    v.min_order_amount ? `orders over ${money(v.min_order_amount)}` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
            ),
          },
          {
            key: "starts_at",
            label: "Valid",
            render: (v) =>
              `${formatDate(v.starts_at, format)}${v.ends_at ? ` to ${formatDate(v.ends_at, format)}` : " on"}`,
          },
          {
            key: "redemptions",
            label: "Used",
            render: (v) => `${usageText(v)}${v.per_customer_limit ? ` · ${v.per_customer_limit} per customer` : ""}`,
          },
          {
            key: "is_active",
            label: "Status",
            render: (v) => (
              <Badge tone={v.is_active ? "success" : "neutral"}>{v.is_active ? "Active" : "Inactive"}</Badge>
            ),
          },
          {
            key: "actions",
            label: "",
            render: (v) => {
              const can = voucherActionsFor(v);
              return (
                <span className={styles.actions}>
                  <TextButton onClick={() => setDialog({ kind: "edit", voucher: v })}>Edit</TextButton>
                  <TextButton onClick={() => setDialog({ kind: "toggle", voucher: v })}>
                    {can.deactivate ? "Switch off" : "Switch on"}
                  </TextButton>
                  {can.remove ? (
                    <TextButton className={styles.danger} onClick={() => setDialog({ kind: "remove", voucher: v })}>
                      Delete
                    </TextButton>
                  ) : null}
                </span>
              );
            },
          },
        ]}
      />
      {dialog?.kind === "edit" ? <VoucherDialog voucher={dialog.voucher} onClose={() => setDialog(null)} /> : null}
      {dialog?.kind === "toggle" ? (
        <ConfirmDialog
          title={dialog.voucher.is_active ? `Switch off ${dialog.voucher.code}?` : `Switch on ${dialog.voucher.code}?`}
          description={
            dialog.voucher.is_active
              ? "Customers can't use it any more. Orders that used it keep their discount."
              : "Customers can use it again, within its dates and limits."
          }
          action={dialog.voucher.is_active ? "Switch off" : "Switch on"}
          run={() =>
            ok(
              api.PATCH("/v1/admin/pricing/vouchers/{id}", {
                params: { path: { id: dialog.voucher.id } },
                body: { is_active: !dialog.voucher.is_active },
              }),
            )
          }
          onDone={refresh}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "remove" ? (
        <ConfirmDialog
          title={`Delete ${dialog.voucher.code}?`}
          description="Nobody has used it, so it goes for good."
          action="Delete"
          danger
          run={() => ok(api.DELETE("/v1/admin/pricing/vouchers/{id}", { params: { path: { id: dialog.voucher.id } } }))}
          onDone={refresh}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}

const count = (v: string) => /^\d{0,6}$/.test(v.trim());
// number-ok: a count of uses, not money
const toCount = (v: string) => (v.trim() ? Number.parseInt(v, 10) : undefined);

/**
 * A new voucher, or a voucher's limits, end date and description. The offer itself (type, amount,
 * item) is fixed once made: make a new voucher to change it.
 */
function VoucherDialog({ voucher, onClose }: { voucher?: Voucher; onClose: () => void }) {
  const format = useCompanyFormat();
  const queryClient = useQueryClient();
  const items = useProductItems();
  const [code, setCode] = useState(voucher?.code ?? "");
  const [type, setType] = useState<"percent" | "fixed">(voucher?.discount_type ?? "percent");
  const [amount, setAmount] = useState(voucher?.amount ?? "");
  const [itemId, setItemId] = useState(voucher?.product_item_id ?? "any");
  const [description, setDescription] = useState(voucher?.description ?? "");
  const [maxDiscount, setMaxDiscount] = useState(voucher?.max_discount ?? "");
  const [minOrder, setMinOrder] = useState(voucher?.min_order_amount ?? "");
  const [usage, setUsage] = useState(voucher?.usage_limit?.toString() ?? "");
  const [perCustomer, setPerCustomer] = useState(voucher?.per_customer_limit?.toString() ?? "");
  // a new voucher can wait for a campaign's first day; once created its start stays
  const [starts, setStarts] = useState<Date | null>(null);
  const [ends, setEnds] = useState<Date | undefined>(
    voucher?.ends_at ? new Date(new Date(voucher.ends_at).setHours(0, 0, 0, 0)) : undefined,
  );
  const [active, setActive] = useState(voucher?.is_active ?? true);
  const optionalAmount = (v: string) => v.trim() === "" || isAmount(v);
  const valid =
    (voucher || (/^[A-Za-z0-9_-]{3,40}$/.test(code.trim()) && isAmount(amount))) &&
    optionalAmount(maxDiscount) &&
    optionalAmount(minOrder) &&
    count(usage) &&
    count(perCustomer);
  // the last moment of the day it ends on, in the company's zone
  const endsAt = ends ? zonedInstant(new Date(ends.getTime() + 86_400_000), "00:00", format.timezone) : undefined;
  const save = useMutation({
    mutationFn: () => {
      const limits = {
        description: description.trim() || undefined,
        max_discount: maxDiscount.trim() || undefined,
        min_order_amount: minOrder.trim() || undefined,
        usage_limit: toCount(usage),
        per_customer_limit: toCount(perCustomer),
        ends_at: endsAt,
      };
      return voucher
        ? ok(
            api.PATCH("/v1/admin/pricing/vouchers/{id}", {
              params: { path: { id: voucher.id } },
              // a field emptied on an edit removes the limit (null); undefined would keep the old one
              body: {
                description: limits.description ?? null,
                max_discount: limits.max_discount ?? null,
                min_order_amount: limits.min_order_amount ?? null,
                usage_limit: limits.usage_limit ?? null,
                per_customer_limit: limits.per_customer_limit ?? null,
                ends_at: limits.ends_at ?? null,
                is_active: active,
              },
            }),
          )
        : ok(
            api.POST("/v1/admin/pricing/vouchers", {
              body: {
                code: code.trim(),
                discount_type: type,
                amount: amount.trim(),
                ...(itemId !== "any" ? { product_item_id: itemId } : {}),
                starts_at: momentOf(starts, format.timezone),
                ...limits,
              },
            }),
          );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["vouchers"] });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={voucher ? `Edit ${voucher.code}` : "New voucher"}
        description={
          voucher
            ? "The offer stays as it is: make a new voucher to change it."
            : "It applies before tax, when the code is entered."
        }
      >
        <div className={styles.form}>
          {voucher ? null : (
            <>
              <Input
                label="Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="WELCOME10"
                description="What customers type. Not case-sensitive; unique in the company."
              />
              <div className={styles.pair}>
                <Select
                  label="Discount"
                  value={type}
                  onValueChange={(v) => setType(v as "percent" | "fixed")}
                  options={[
                    { value: "percent", label: "A percentage" },
                    { value: "fixed", label: "A fixed amount" },
                  ]}
                />
                <Input
                  label={type === "percent" ? "Percent off" : `Amount off (${format.currency})`}
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
              <Select
                label="On"
                value={itemId}
                onValueChange={setItemId}
                options={[
                  { value: "any", label: "The whole order" },
                  ...items.items.map((i) => ({ value: i.id, label: i.label })),
                ]}
              />
            </>
          )}
          <Input label="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
          <div className={styles.pair}>
            {type === "percent" ? (
              <Input
                label="At most (optional)"
                inputMode="decimal"
                value={maxDiscount}
                onChange={(e) => setMaxDiscount(e.target.value)}
                description="The biggest discount it gives"
              />
            ) : null}
            <Input
              label="Orders over (optional)"
              inputMode="decimal"
              value={minOrder}
              onChange={(e) => setMinOrder(e.target.value)}
              description="Before tax"
            />
          </div>
          <div className={styles.pair}>
            <Input
              label="Uses in all (optional)"
              inputMode="numeric"
              value={usage}
              onChange={(e) => setUsage(e.target.value)}
            />
            <Input
              label="Uses per customer (optional)"
              inputMode="numeric"
              value={perCustomer}
              onChange={(e) => setPerCustomer(e.target.value)}
            />
          </div>
          {voucher ? null : <MomentField label="Starts" value={starts} onChange={setStarts} />}
          <DatePicker
            label="Last day (optional)"
            value={ends}
            onChange={setEnds}
            minDate={new Date()}
            placeholder="No end"
          />
          {ends ? <TextButton onClick={() => setEnds(undefined)}>No end date</TextButton> : null}
          {voucher ? (
            <Switch label="Active: customers can use it" checked={active} onCheckedChange={setActive} />
          ) : null}
          {save.error ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(errorOf(save.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
              {voucher ? "Save" : "Make the voucher"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
